import React, { useState, useEffect, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { useTheme } from '../contexts/ThemeContext';

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_email: string;
  is_active: boolean;
  sync_status: string;
  last_sync_at?: string;
  created_at: string;
}

const Integrations: React.FC = () => {
  const { effectiveTheme } = useTheme();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getProviderDisplayName = useCallback((provider: string) => {
    const names: { [key: string]: string } = {
      'google-drive': 'Google Drive',
      'onedrive': 'OneDrive',
      'box': 'Box',
      'dropbox': 'Dropbox',
      'egnyte': 'Egnyte',
      'salesforce': 'Salesforce',
      'quickbooks': 'QuickBooks',
      'xero': 'Xero',
      'deal_cloud': 'Deal Cloud',
      'affinity': 'Affinity'
    };
    return names[provider] || provider;
  }, []);

  const loadIntegrations = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiService.getIntegrations();
      setIntegrations(response);
    } catch (error) {
      console.error('Error loading integrations:', error);
      notificationService.error('Failed to load integrations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleOAuthCallback = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const provider = urlParams.get('provider');
    const error = urlParams.get('error');

    if (status === 'success' && provider) {
      const providerName = getProviderDisplayName(provider);
      notificationService.success(`${providerName} connected successfully!`);
      
      // Reload integrations to show the new connection
      setTimeout(() => {
        loadIntegrations();
      }, 1000);
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (status === 'error' && provider) {
      const providerName = getProviderDisplayName(provider);
      const errorMessage = error ? decodeURIComponent(error) : 'Connection failed';
      notificationService.error(`Failed to connect ${providerName}: ${errorMessage}`);
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadIntegrations, getProviderDisplayName]);

  useEffect(() => {
    loadIntegrations();
    handleOAuthCallback();
  }, [handleOAuthCallback, loadIntegrations]);

  const connectIntegration = async (provider: string) => {
    try {
      const response = await apiService.getIntegrationAuthUrl(provider);
      
      // Open OAuth popup
      const popup = window.open(
        response.auth_url, 
        'oauth', 
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      // Listen for OAuth completion
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          // Reload integrations after OAuth with a longer delay
          setTimeout(async () => {
            await loadIntegrations();
            notificationService.success(`${getProviderDisplayName(provider)} connected successfully!`);
          }, 2000);
        }
      }, 500); // Check more frequently

      // Also listen for messages from the popup (if the OAuth flow sends postMessage)
      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'oauth_success') {
          clearInterval(checkClosed);
          popup?.close();
          window.removeEventListener('message', messageListener);
          
          setTimeout(async () => {
            await loadIntegrations();
            notificationService.success(`${getProviderDisplayName(provider)} connected successfully!`);
          }, 1000);
        }
      };
      
      window.addEventListener('message', messageListener);

      // Clean up listener after 5 minutes
      setTimeout(() => {
        window.removeEventListener('message', messageListener);
      }, 5 * 60 * 1000);

    } catch (error) {
      console.error(`Error connecting ${provider}:`, error);
      notificationService.error(`Failed to connect ${getProviderDisplayName(provider)}`);
    }
  };

  const disconnectIntegration = async (integrationId: number, providerName: string) => {
    try {
      await apiService.disconnectIntegration(integrationId);
      notificationService.success(`${providerName} disconnected successfully`);
      loadIntegrations();
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      notificationService.error('Failed to disconnect integration');
    }
  };

  const testIntegration = async (integrationId: number, providerName: string) => {
    try {
      const response = await apiService.testIntegration(integrationId);
      if (response.is_working) {
        notificationService.success(`${providerName} connection is working`);
      } else {
        notificationService.warning(`${providerName} connection has issues`);
      }
    } catch (error) {
      console.error('Error testing integration:', error);
      notificationService.error('Failed to test integration');
    }
  };


  const getProviderIcon = useCallback((provider: string) => {
    const icons: { [key: string]: string } = {
      'google-drive': 'bi-google',
      'onedrive': 'bi-microsoft',
      'box': 'bi-box',
      'dropbox': 'bi-dropbox',
      'egnyte': 'bi-cloud-arrow-up',
      'salesforce': 'bi-cloud',
      'quickbooks': 'bi-calculator',
      'xero': 'bi-graph-up',
      'deal_cloud': 'bi-briefcase',
      'affinity': 'bi-people'
    };
    return icons[provider] || 'bi-cloud';
  }, []);

  const getProviderColor = useCallback((provider: string) => {
    const colors: { [key: string]: string } = {
      'google-drive': '#4285f4',
      'onedrive': '#0078d4',
      'box': '#0061d5',
      'dropbox': '#0061ff',
      'egnyte': '#7b68ee',
      'salesforce': '#00a1e0',
      'quickbooks': '#0077c5',
      'xero': '#13b5ea',
      'deal_cloud': '#ff6b35',
      'affinity': '#6366f1'
    };
    return colors[provider] || '#6366f1';
  }, []);

  const getProviderDescription = useCallback((provider: string) => {
    const descriptions: { [key: string]: string } = {
      'google-drive': 'Access and import files from Google Drive',
      'onedrive': 'Access and import files from OneDrive and SharePoint',
      'box': 'Access and import files from Box cloud storage',
      'dropbox': 'Access and import files from Dropbox',
      'egnyte': 'Access and import files from Egnyte secure file sharing',
      'salesforce': 'Connect to Salesforce for CRM data and documents',
      'quickbooks': 'Connect to QuickBooks for financial data and reports',
      'xero': 'Connect to Xero for accounting data and financial reports',
      'deal_cloud': 'Connect to Deal Cloud for deal management and documents',
      'affinity': 'Connect to Affinity for relationship intelligence and deal data'
    };
    return descriptions[provider] || 'Connect to this service';
  }, []);

  const availableIntegrations = [
    'google-drive',
    'onedrive', 
    'box',
    'dropbox',
    'egnyte',
    'salesforce',
    'quickbooks',
    'xero',
    'deal_cloud',
    'affinity'
  ];

  const getProviderStatus = (provider: string) => {
    const comingSoon = ['deal_cloud', 'affinity'];
    return comingSoon.includes(provider) ? 'coming_soon' : 'available';
  };

  const connectedProviders = new Set(integrations.map(i => i.provider));

  if (isLoading) {
    return (
      <div style={{ padding: '2rem' }}>
        {/* Header Skeleton */}
        <div style={{ marginBottom: '2rem' }}>
          <Skeleton height={30} width="30%" style={{ marginBottom: '0.5rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <Skeleton height={20} width="50%" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
        </div>

        {/* Connected Integrations Skeleton */}
        <div style={{ marginBottom: '2rem' }}>
          <Skeleton height={25} width="20%" style={{ marginBottom: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1.5rem'
          }}>
            {Array.from({ length: 2 }).map((_, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Skeleton circle height={48} width={48} style={{ marginRight: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    <div>
                      <Skeleton height={18} width="150px" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                      <Skeleton height={14} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </div>
                  </div>
                  <Skeleton height={20} width="80px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton height={14} width="120px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Skeleton height={30} width="70px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    <Skeleton height={30} width="90px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Integrations Skeleton */}
        <div>
          <Skeleton height={25} width="25%" style={{ marginBottom: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '1.5rem'
          }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <Skeleton circle height={48} width={48} style={{ marginRight: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <div style={{ flex: 1 }}>
                    <Skeleton height={18} width="180px" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    <Skeleton height={14} width="250px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton height={20} width="90px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                  <Skeleton height={38} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Stats Skeleton */}
        <div style={{ marginTop: '2rem' }}>
          <Skeleton height={25} width="30%" style={{ marginBottom: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <div style={{
            background: 'var(--bg-color)',
            border: '1px solid var(--border-color)',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '2rem'
          }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <Skeleton circle height={60} width={60} style={{ margin: '0 auto 1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                <Skeleton height={20} width="80px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
              </div>
            ))}
          </div>
        </div>

        {/* Help Section Skeleton */}
        <div style={{ marginTop: '2rem' }}>
          <Skeleton height={25} width="25%" style={{ marginBottom: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height={15} width="90%" style={{ marginBottom: '0.75rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontWeight: 600 }}>Integrations</h2>
        <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '1rem' }}>
          Connect your cloud storage and business applications to import data
        </p>
      </div>

      {/* Connected Integrations */}
      {integrations.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h5 style={{ color: 'var(--text-primary)', marginBottom: '1rem', fontWeight: 600 }}>
            <i className="bi bi-check-circle" style={{ color: 'var(--success-color)', marginRight: '0.5rem' }}></i>
            Connected ({integrations.length})
          </h5>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {integrations.map((integration) => (
              <div 
                key={integration.id}
                style={{
                  background: 'var(--bg-color)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgb(0 0 0 / 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '0.5rem',
                      background: getProviderColor(integration.provider),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '1rem'
                    }}>
                      <i className={`bi ${getProviderIcon(integration.provider)}`} style={{ 
                        fontSize: '1.5rem', 
                        color: 'white' 
                      }}></i>
                    </div>
                    <div>
                      <h6 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem 0', fontWeight: 600 }}>
                        {getProviderDisplayName(integration.provider)}
                      </h6>
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {integration.provider_email}
                      </small>
                    </div>
                  </div>
                  <div style={{
                    background: integration.sync_status === 'completed' ? 'var(--success-bg)' : 'var(--warning-bg)',
                    color: integration.sync_status === 'completed' ? 'var(--success-color)' : 'var(--warning-color)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'capitalize'
                  }}>
                    {integration.sync_status}
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  <small style={{ color: 'var(--text-secondary)' }}>
                    Connected {new Date(integration.created_at).toLocaleDateString()}
                  </small>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => testIntegration(integration.id, integration.provider_name)}
                      style={{
                        background: 'var(--bg-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        color: 'var(--text-secondary)',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      <i className="bi bi-arrow-clockwise" style={{ marginRight: '0.25rem' }}></i>
                      Test
                    </button>
                    <button
                      onClick={() => disconnectIntegration(integration.id, integration.provider_name)}
                      style={{
                        background: 'var(--danger-bg)',
                        border: '1px solid var(--danger-border)',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 0.75rem',
                        color: 'var(--danger-color)',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      <i className="bi bi-x" style={{ marginRight: '0.25rem' }}></i>
                      Disconnect
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available Integrations */}
      <div>
        <h5 style={{ color: 'var(--text-primary)', marginBottom: '1rem', fontWeight: 600 }}>
          <i className="bi bi-plus-circle" style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}></i>
          Available Integrations
        </h5>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
          gap: '1.5rem' 
        }}>
          {availableIntegrations.map((provider) => {
            const isConnected = connectedProviders.has(provider);
            
            return (
              <div 
                key={provider}
                style={{
                  background: 'var(--bg-color)',
                  border: isConnected ? '1px solid var(--success-color)' : '1px solid var(--border-color)',
                  borderRadius: '0.75rem',
                  padding: '1.5rem',
                  opacity: isConnected ? 0.7 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isConnected) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 10px 25px -5px rgb(0 0 0 / 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isConnected) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '0.5rem',
                    background: getProviderColor(provider),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem'
                  }}>
                    <i className={`bi ${getProviderIcon(provider)}`} style={{ 
                      fontSize: '1.5rem', 
                      color: 'white' 
                    }}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h6 style={{ color: 'var(--text-primary)', margin: '0 0 0.25rem 0', fontWeight: 600 }}>
                      {getProviderDisplayName(provider)}
                    </h6>
                    <p style={{
                      color: 'var(--text-secondary)',
                      fontSize: '0.85rem',
                      margin: 0,
                      lineHeight: 1.4
                    }}>
                      {getProviderDescription(provider)}
                    </p>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center' 
                }}>
                  {isConnected ? (
                    <div style={{
                      background: 'var(--success-bg)',
                      color: 'var(--success-color)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}>
                      <i className="bi bi-check-circle" style={{ marginRight: '0.25rem' }}></i>
                      Connected
                    </div>
                  ) : (
                    <div></div>
                  )}
                  
                  <button
                    onClick={() => connectIntegration(provider)}
                    disabled={isConnected || getProviderStatus(provider) === 'coming_soon'}
                    style={{
                      background: isConnected
                        ? 'var(--text-muted)'
                        : getProviderStatus(provider) === 'coming_soon'
                          ? 'var(--bg-secondary)'
                          : getProviderColor(provider),
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '0.75rem 1.25rem',
                      color: getProviderStatus(provider) === 'coming_soon' ? 'var(--text-secondary)' : 'white',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      cursor: (isConnected || getProviderStatus(provider) === 'coming_soon') ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isConnected && getProviderStatus(provider) !== 'coming_soon') {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isConnected && getProviderStatus(provider) !== 'coming_soon') {
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {isConnected ? (
                      <>
                        <i className="bi bi-check" style={{ marginRight: '0.5rem' }}></i>
                        Connected
                      </>
                    ) : getProviderStatus(provider) === 'coming_soon' ? (
                      <>
                        <i className="bi bi-clock" style={{ marginRight: '0.5rem' }}></i>
                        Coming Soon
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus" style={{ marginRight: '0.5rem' }}></i>
                        Connect
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Integration Stats */}
      {integrations.length > 0 && (
        <div style={{
          marginTop: '2rem',
          background: 'var(--bg-color)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.75rem',
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)'
          }}>
            <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
              <i className="bi bi-bar-chart" style={{ marginRight: '0.5rem' }}></i>
              Integration Statistics
            </h5>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '2rem' 
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'var(--bg-secondary)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--primary-color)' }}>
                    {integrations.length}
                  </span>
                </div>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Total Connected</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'var(--success-bg)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--success-color)' }}>
                    {integrations.filter(i => i.is_active).length}
                  </span>
                </div>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Active</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'var(--info-bg)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--info-color)' }}>
                    {integrations.filter(i => i.sync_status === 'completed').length}
                  </span>
                </div>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Synced</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'var(--warning-bg)',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem'
                }}>
                  <span style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--warning-color)' }}>
                    {availableIntegrations.length - integrations.length}
                  </span>
                </div>
                <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>Available</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Section */}
      <div style={{
        marginTop: '2rem',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h5 style={{ color: 'var(--text-primary)', marginBottom: '1rem', fontWeight: 600 }}>
          <i className="bi bi-info-circle" style={{ color: 'var(--primary-color)', marginRight: '0.5rem' }}></i>
          How Integrations Work
        </h5>
        <ul style={{ color: 'var(--text-secondary)', margin: 0, paddingLeft: '1.5rem', lineHeight: 1.6 }}>
          <li style={{ marginBottom: '0.75rem' }}>
            Connect your cloud storage accounts to import documents directly into projects
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            Business application integrations provide additional context for document analysis
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            All imported files are processed and become available for AI analysis
          </li>
          <li>
            Integration tokens are securely stored and automatically refreshed
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Integrations;
